import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { NavLink, useLocation } from 'react-router-dom';
import { listPipelineRuns } from '@/services/pipelineApi';
import type { PipelineRunResponse } from '@/types/pipeline';
import { usePresetNames, resolveRunDisplayName } from '../utils/PresetNameContext';

const DB_RUNS_KEY = 'qs_databricks_runs';

interface RecentDatabricksRun {
  run_id: string;
  output_path: string;
  workspace_id: string;
  template_name: string;
  input_file_path: string;
  launched_at: string;
  status?: 'running' | 'done' | 'failed';
}

function loadDbRuns(): RecentDatabricksRun[] {
  try {
    return JSON.parse(localStorage.getItem(DB_RUNS_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function dbRunStatusColor(status?: string): string {
  if (status === 'done') return 'var(--accessible-green)';
  if (status === 'failed') return 'var(--red)';
  return 'var(--accessible-blue)';
}

function fmtTemplateName(name: string): string {
  return name.replaceAll('_', ' ').replaceAll(/\b\w/g, c => c.toUpperCase());
}

const STORAGE_KEY = 'qs-sidenav-collapsed';
const ANALYSES_OPEN_KEY = 'qs-analyses-open';
const THEME_KEY = 'quant-studio-theme';

type ThemeMode = 'auto' | 'light' | 'dark';
const THEME_CYCLE: ThemeMode[] = ['auto', 'light', 'dark'];

function applyTheme(mode: ThemeMode) {
  const html = document.documentElement;
  html.classList.remove('light-mode', 'dark-mode');
  if (mode === 'light') html.classList.add('light-mode');
  if (mode === 'dark') html.classList.add('dark-mode');
}

function statusColor(status: string): string {
  if (status === 'completed') return 'var(--accessible-green)';
  if (status === 'failed') return 'var(--red)';
  if (status === 'running') return 'var(--accessible-blue)';
  if (status === 'interrupted') return 'var(--orange)';
  return 'var(--cg-6)';
}

export interface SideNavProps {
  projectName?: string;
  clientName?: string;
  collapsed?: boolean;
  onToggle?: () => void;
}

const Sidebar = styled.nav`
  position: fixed;
  top: var(--host-header-h, 0px);
  left: 0;
  bottom: var(--host-footer-h, 0px);
  width: var(--sidenav-w);
  background: var(--nav-surface);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width 200ms ease;
  z-index: 90;

  &::after {
    content: '';
    position: fixed;
    left: 0;
    bottom: 0;
    width: var(--sidenav-w);
    height: var(--host-footer-h, 0px);
    background: var(--nav-surface);
    z-index: 89;
    transition: width 200ms ease;
  }
`;

const BrandArea = styled.div`
  flex-shrink: 0;
  overflow: hidden;
`;

const Wordmark = styled.span`
  font-family: var(--font-family);
  font-size: 11px;
  font-weight: 300;
  color: var(--cg-6);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  display: block;
`;

const ProductLabel = styled.span`
  font-family: var(--font-family);
  font-size: 14px;
  font-weight: 700;
  color: var(--green);
  white-space: nowrap;
  display: block;
  margin-top: 2px;
`;

const CollapsedMark = styled.span`
  font-family: var(--font-family);
  font-size: 16px;
  font-weight: 700;
  color: var(--green);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 38px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 0 var(--space-3);
  flex-shrink: 0;
`;

const NavList = styled.ul`
  list-style: none;
  margin: var(--space-1) 0 0;
  padding: 0 0 var(--space-2);
  flex-shrink: 0;
`;

const StyledNavLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  text-decoration: none;
  font-family: var(--font-family);
  font-size: 13px;
  font-weight: 400;
  color: var(--cg-6);
  border-left: 3px solid transparent;
  transition: color 120ms ease, background 120ms ease, border-left-color 120ms ease;
  white-space: nowrap;

  &:hover {
    color: var(--nav-text-active);
    background: rgba(255, 255, 255, 0.06);
  }

  &.active {
    color: var(--nav-text-active);
    font-weight: 600;
    border-left-color: var(--accessible-blue);
  }
`;

const NavIcon = styled.span`
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
  width: 20px;
  text-align: center;
`;

const NavLabel = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ── Analyses section ──────────────────────────────────────────────────────────

const AnalysesSection = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const AnalysesHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  background: none;
  border: none;
  flex-shrink: 0;
  transition: background 120ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }

  &:focus-visible {
    outline: 2px solid var(--accessible-blue);
    outline-offset: -2px;
  }
`;

const AnalysesLabel = styled.span`
  font-family: var(--font-family);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--cg-6);
  flex: 1;
  text-align: left;
`;

const AnalysesCaret = styled.span`
  font-size: 10px;
  color: var(--cg-6);
  flex-shrink: 0;
`;

const AnalysesScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
`;

const AnalysisLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  text-decoration: none;
  transition: background 120ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }

  &.active {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const StatusDot = styled.span<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const AnalysisName = styled.span`
  font-family: var(--font-family);
  font-size: 11px;
  font-weight: 400;
  color: var(--cg-6);
  white-space: normal;
  word-break: break-word;
  flex: 1;
  line-height: 1.4;
`;

const AnalysesEmpty = styled.p`
  font-family: var(--font-family);
  font-size: 12px;
  color: var(--cg-6);
  margin: 0;
  opacity: 0.7;
  white-space: nowrap;
`;

// ── Footer ────────────────────────────────────────────────────────────────────

const SideNavFooter = styled.div`
  flex-shrink: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: var(--space-2);
`;

const FooterThemeBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: var(--radius-1);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: var(--cg-6);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: var(--nav-text-active);
  }

  &:focus-visible {
    outline: 2px solid var(--accessible-blue);
    outline-offset: 2px;
  }
`;

const FooterThemeLabel = styled.span`
  font-family: var(--font-family);
  font-size: 12px;
  color: var(--cg-6);
  flex: 1;
  white-space: nowrap;
`;

const ToggleButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: var(--radius-1);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: var(--cg-6);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: var(--nav-text-active);
  }

  &:focus-visible {
    outline: 2px solid var(--accessible-blue);
    outline-offset: 2px;
  }
`;

const NAV_ITEMS = [
  { to: '/pipeline', label: 'New Analysis', icon: '⊕', end: true },
  { to: '/pipeline/analyses', label: 'All Analyses', icon: '≡', end: false },
  { to: '/pipeline/data-sources', label: 'Data sources', icon: '⊞', end: false },
  { to: '/pipeline/presets', label: 'My Presets', icon: '☆', end: false },
] as const;

// ── Module-level helpers (extracted to keep SideNav cognitive complexity low) ──

function handleSideNavToggle(
  onToggle: (() => void) | undefined,
  setCollapsedInternal: React.Dispatch<React.SetStateAction<boolean>>,
): void {
  if (onToggle) {
    onToggle();
    return;
  }
  setCollapsedInternal(prev => {
    const next = !prev;
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // storage unavailable
    }
    return next;
  });
}

function handleAnalysesToggle(setAnalysesOpen: React.Dispatch<React.SetStateAction<boolean>>): void {
  setAnalysesOpen(prev => {
    const next = !prev;
    try {
      localStorage.setItem(ANALYSES_OPEN_KEY, String(next));
    } catch {
      // storage unavailable
    }
    return next;
  });
}

type UnifiedRun =
  | { kind: 'local'; run: PipelineRunResponse }
  | { kind: 'db'; run: RecentDatabricksRun };

function buildMergedRuns(
  databricksRuns: RecentDatabricksRun[],
  runs: PipelineRunResponse[],
): UnifiedRun[] {
  return [
    ...databricksRuns.map(r => ({ kind: 'db' as const, run: r })),
    ...runs.map(r => ({ kind: 'local' as const, run: r })),
  ].sort((a, b) => {
    const ta = new Date(a.kind === 'local' ? a.run.created_at : a.run.launched_at).getTime();
    const tb = new Date(b.kind === 'local' ? b.run.created_at : b.run.launched_at).getTime();
    return tb - ta;
  });
}

function buildDisplayNames(
  merged: UnifiedRun[],
  presetNameMap: Map<string, string>,
): string[] {
  const rawNames = merged.map(item =>
    item.kind === 'db'
      ? fmtTemplateName(item.run.template_name)
      : resolveRunDisplayName(item.run, presetNameMap),
  );

  // Count total occurrences of each base name
  const totalCount = new Map<string, number>();
  for (const name of rawNames) {
    totalCount.set(name, (totalCount.get(name) ?? 0) + 1);
  }

  // Newest run (top of list) gets the highest number; oldest run (bottom) gets no suffix.
  const seen = new Map<string, number>();
  return rawNames.map(name => {
    const total = totalCount.get(name) ?? 1;
    if (total === 1) return name;
    const n = (seen.get(name) ?? 0) + 1;
    seen.set(name, n);
    const displayN = total - n + 1; // newest → total, oldest → 1 (no suffix)
    return displayN === 1 ? name : `${name} (${displayN})`;
  });
}

function AnalysesList({
  runs,
  databricksRuns,
}: Readonly<{ runs: PipelineRunResponse[]; databricksRuns: RecentDatabricksRun[] }>) {
  const { presetNameMap } = usePresetNames();
  const merged = buildMergedRuns(databricksRuns, runs);
  if (merged.length === 0) {
    return (
      <AnalysesEmpty style={{ padding: '6px var(--space-4)' }}>
        No analyses yet
      </AnalysesEmpty>
    );
  }
  const displayNames = buildDisplayNames(merged, presetNameMap);
  return (
    <>
      {merged.map((item, idx) =>
        item.kind === 'db' ? (
          <AnalysisLink
            key={`db-${item.run.run_id}`}
            to={`/pipeline/databricks-run/${item.run.run_id}?output_path=${encodeURIComponent(item.run.output_path)}&workspace_id=${encodeURIComponent(item.run.workspace_id)}`}
            style={{ padding: '6px var(--space-4)' }}
          >
            <StatusDot $color={dbRunStatusColor(item.run.status)} />
            <AnalysisName title={displayNames[idx]}>{displayNames[idx]}</AnalysisName>
          </AnalysisLink>
        ) : (
          <AnalysisLink
            key={item.run.id}
            to={`/pipeline/run/${item.run.id}`}
            style={{ padding: '6px var(--space-4)' }}
          >
            <StatusDot $color={statusColor(item.run.status)} />
            <AnalysisName title={displayNames[idx]}>{displayNames[idx]}</AnalysisName>
          </AnalysisLink>
        )
      )}
    </>
  );
}

function getThemeDisplay(mode: ThemeMode): { icon: string; label: string } {
  if (mode === 'light') return { icon: '☀', label: 'Light' };
  if (mode === 'dark') return { icon: '☽', label: 'Dark' };
  return { icon: '◑', label: 'Auto' };
}

function getBrandAreaStyle(collapsed: boolean): React.CSSProperties {
  return {
    padding: collapsed ? 'var(--space-3) 0' : 'var(--space-4) var(--space-4) var(--space-3)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: collapsed ? 'center' : 'flex-start',
  };
}

function getAnalysesHeaderStyle(collapsed: boolean): React.CSSProperties {
  return {
    padding: collapsed ? '10px 0' : '10px var(--space-4)',
    justifyContent: collapsed ? 'center' : 'flex-start',
    cursor: collapsed ? 'default' : 'pointer',
    gap: collapsed ? undefined : 'var(--space-2)',
  };
}

export function SideNav({ projectName: _projectName, clientName: _clientName, collapsed: collapsedProp, onToggle }: Readonly<SideNavProps>) {
  const location = useLocation();

  const [collapsedInternal, setCollapsedInternal] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [analysesOpen, setAnalysesOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ANALYSES_OPEN_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  const [runs, setRuns] = useState<PipelineRunResponse[]>([]);
  const [databricksRuns, setDatabricksRuns] = useState<RecentDatabricksRun[]>(() => loadDbRuns());

  useEffect(() => {
    setDatabricksRuns(loadDbRuns());
  }, [location.pathname]);

  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    try {
      const stored = localStorage.getItem(THEME_KEY) as ThemeMode | null;
      return stored && THEME_CYCLE.includes(stored) ? stored : 'auto';
    } catch {
      return 'auto';
    }
  });

  useEffect(() => {
    applyTheme(themeMode);
    try {
      localStorage.setItem(THEME_KEY, themeMode);
    } catch {
      // storage unavailable
    }
  }, [themeMode]);

  useEffect(() => {
    listPipelineRuns().then(setRuns).catch(() => {});
    const interval = setInterval(() => {
      listPipelineRuns().then(setRuns).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const collapsed = collapsedProp ?? collapsedInternal;

  const toggle = () => handleSideNavToggle(onToggle, setCollapsedInternal);
  const toggleAnalyses = () => handleAnalysesToggle(setAnalysesOpen);
  const cycleTheme = () => setThemeMode(prev => THEME_CYCLE[(THEME_CYCLE.indexOf(prev) + 1) % THEME_CYCLE.length]);

  const { icon: themeIcon, label: themeLabel } = getThemeDisplay(themeMode);

  const linkPadding = collapsed ? '10px 0' : '10px var(--space-4)';
  const linkJustify = collapsed ? 'center' : 'flex-start';

  return (
    <Sidebar
      aria-label="Main navigation"
      style={{ '--sidenav-w': collapsed ? '56px' : '240px' } as React.CSSProperties}
    >
      <BrandArea style={getBrandAreaStyle(collapsed)}>
        {collapsed ? (
          <CollapsedMark>Q</CollapsedMark>
        ) : (
          <>
            <Wordmark>Deloitte Ascend</Wordmark>
            <ProductLabel>Quant Studio</ProductLabel>
          </>
        )}
      </BrandArea>

      <Divider />

      <NavList>
        {NAV_ITEMS.map(item => (
          <li key={item.to}>
            <StyledNavLink
              to={item.to}
              end={item.end}
              style={{ padding: linkPadding, justifyContent: linkJustify }}
            >
              <NavIcon>{item.icon}</NavIcon>
              {!collapsed && <NavLabel>{item.label}</NavLabel>}
            </StyledNavLink>
          </li>
        ))}
      </NavList>

      <Divider />

      <AnalysesSection>
        <AnalysesHeader
          onClick={collapsed ? undefined : toggleAnalyses}
          style={getAnalysesHeaderStyle(collapsed)}
          aria-label="Toggle analyses list"
        >
          {collapsed ? (
            <NavIcon>≡</NavIcon>
          ) : (
            <>
              <AnalysesLabel>Analyses</AnalysesLabel>
              <AnalysesCaret>{analysesOpen ? '▾' : '▸'}</AnalysesCaret>
            </>
          )}
        </AnalysesHeader>

        {!collapsed && analysesOpen && (
          <AnalysesScroll>
            <AnalysesList runs={runs} databricksRuns={databricksRuns} />
          </AnalysesScroll>
        )}
      </AnalysesSection>

      <SideNavFooter
        style={{
          padding: collapsed ? 'var(--space-2) 0' : 'var(--space-2) var(--space-3)',
          flexDirection: collapsed ? 'column' : 'row',
          justifyContent: 'center',
        }}
      >
        <FooterThemeBtn
          onClick={cycleTheme}
          title={`Theme: ${themeLabel}`}
          aria-label={`Theme: ${themeLabel}`}
        >
          {themeIcon}
        </FooterThemeBtn>
        {!collapsed && <FooterThemeLabel>{themeLabel}</FooterThemeLabel>}
        <ToggleButton
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{ marginLeft: collapsed ? undefined : 'auto' }}
        >
          {collapsed ? '›' : '‹'}
        </ToggleButton>
      </SideNavFooter>
    </Sidebar>
  );
}
